import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Globe, MapPin, Calendar, Briefcase, Hash, Target, Package, ShieldCheck, Lightbulb, Compass } from 'lucide-react';
import { useWorkspace } from '../../lib/workspace';
import { useApi } from '../../lib/api';
import { useTheme } from '../../lib/ThemeContext';
import { formatDate } from '../../lib/format';

function parseValues(raw) {
  if (!raw) return [];
  try { const a = JSON.parse(raw); return Array.isArray(a) ? a : []; }
  catch { return []; }
}

const VALUE_COLORS_LIGHT = [
  { bg: '#eff6ff', border: '#bfdbfe', dot: '#2563eb', text: '#1e40af' },
  { bg: '#f0fdf4', border: '#bbf7d0', dot: '#16a34a', text: '#166534' },
  { bg: '#fdf4ff', border: '#e9d5ff', dot: '#9333ea', text: '#7e22ce' },
  { bg: '#fff7ed', border: '#fed7aa', dot: '#ea580c', text: '#9a3412' },
  { bg: '#fef2f2', border: '#fecaca', dot: '#dc2626', text: '#991b1b' },
  { bg: '#f0fdfa', border: '#99f6e4', dot: '#0d9488', text: '#115e59' },
];

const VALUE_COLORS_DARK = [
  { bg: 'rgba(37,99,235,0.12)',  border: 'rgba(37,99,235,0.28)',  dot: '#60a5fa', text: '#93c5fd' },
  { bg: 'rgba(22,163,74,0.12)',  border: 'rgba(22,163,74,0.28)',  dot: '#4ade80', text: '#86efac' },
  { bg: 'rgba(147,51,234,0.12)', border: 'rgba(147,51,234,0.28)', dot: '#c084fc', text: '#d8b4fe' },
  { bg: 'rgba(234,88,12,0.12)',  border: 'rgba(234,88,12,0.28)',  dot: '#fb923c', text: '#fdba74' },
  { bg: 'rgba(220,38,38,0.12)',  border: 'rgba(220,38,38,0.28)',  dot: '#f87171', text: '#fca5a5' },
  { bg: 'rgba(13,148,136,0.12)', border: 'rgba(13,148,136,0.28)', dot: '#2dd4bf', text: '#5eead4' },
];

export default function MyOrganisation() {
  const { appId }  = useParams();
  const api        = useApi();
  const { organisation } = useWorkspace();
  const { theme }  = useTheme();
  const isDark     = theme !== 'light';
  const VALUE_COLORS = isDark ? VALUE_COLORS_DARK : VALUE_COLORS_LIGHT;
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    api.locations.list(appId)
      .then((r) => setLocations(r.items || []))
      .catch(() => setLocations([]));
  }, [api, appId]);

  if (!organisation) return <p className="muted">Loading…</p>;

  const logoSrc    = organisation.logo_data || organisation.logo_url || null;
  const coreValues = parseValues(organisation.core_values);
  const initials   = (organisation.name || 'O').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  const metaItems = [
    organisation.industry      && { Icon: Briefcase, label: organisation.industry },
    organisation.founded_on    && { Icon: Calendar,  label: `Est. ${formatDate(organisation.founded_on)}` },
    organisation.registration_no && { Icon: Hash,    label: organisation.registration_no },
    organisation.website       && { Icon: Globe,     label: organisation.website.replace(/^https?:\/\//, ''), href: organisation.website.startsWith('http') ? organisation.website : `https://${organisation.website}` },
    organisation.hq_address    && { Icon: MapPin,    label: organisation.hq_address },
  ].filter(Boolean);

  return (
    <div style={{ paddingBottom: 56 }}>

      {/* ── Hero banner ── */}
      <div style={{
        borderRadius: 12, marginBottom: 20,
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        padding: '28px 28px 24px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 22, flexWrap: 'wrap' }}>
          {/* Logo */}
          <div style={{
            width: 72, height: 72, borderRadius: 12, flexShrink: 0,
            background: 'var(--bg-muted)', border: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', fontSize: 24, fontWeight: 800, color: 'var(--brand)',
          }}>
            {logoSrc
              ? <img src={logoSrc} alt={organisation.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              : initials}
          </div>

          {/* Name + legal */}
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1.2 }}>
              {organisation.name}
            </h1>
            {organisation.legal_name && organisation.legal_name !== organisation.name && (
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
                {organisation.legal_name}
              </div>
            )}
          </div>
        </div>

        {/* Meta pills */}
        {metaItems.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 18 }}>
            {metaItems.map(({ Icon, label, href }, i) => (
              <MetaPill key={i} Icon={Icon} label={label} href={href} />
            ))}
          </div>
        )}
      </div>

      {/* ── About ── */}
      {organisation.description && (
        <div style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 12, padding: '22px 26px', marginBottom: 20,
        }}>
          <SectionLabel>About</SectionLabel>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.75 }}>
            {organisation.description}
          </p>
        </div>
      )}

      {/* ── Vision & Mission ── */}
      {(organisation.vision || organisation.mission) && (
        <div style={{ display: 'grid', gridTemplateColumns: organisation.vision && organisation.mission ? '1fr 1fr' : '1fr', gap: 16, marginBottom: 20 }}>
          {organisation.vision && (
            <QuoteCard
              icon={<Lightbulb size={18} color={isDark ? '#60a5fa' : '#2563eb'} />}
              label="Vision"
              text={organisation.vision}
              accentColor={isDark ? '#60a5fa' : '#2563eb'}
              bgColor={isDark ? 'rgba(37,99,235,0.1)' : '#eff6ff'}
              borderColor={isDark ? 'rgba(37,99,235,0.25)' : '#bfdbfe'}
            />
          )}
          {organisation.mission && (
            <QuoteCard
              icon={<Compass size={18} color={isDark ? '#22d3ee' : '#0891b2'} />}
              label="Mission"
              text={organisation.mission}
              accentColor={isDark ? '#22d3ee' : '#0891b2'}
              bgColor={isDark ? 'rgba(8,145,178,0.1)' : '#ecfeff'}
              borderColor={isDark ? 'rgba(8,145,178,0.25)' : '#a5f3fc'}
            />
          )}
        </div>
      )}

      {/* ── Core Values ── */}
      {coreValues.length > 0 && (
        <div style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 12, padding: '22px 26px', marginBottom: 20,
        }}>
          <SectionLabel>Core Values</SectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {coreValues.map((cv, i) => {
              const c = VALUE_COLORS[i % VALUE_COLORS.length];
              return (
                <div key={i} style={{
                  background: c.bg, border: `1px solid ${c.border}`,
                  borderRadius: 10, padding: '14px 18px',
                  borderLeft: `4px solid ${c.dot}`,
                  display: 'flex', alignItems: 'flex-start', gap: 16,
                }}>
                  {/* Name */}
                  <div style={{ minWidth: 160, flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: c.dot, flexShrink: 0 }} />
                      <span style={{ fontWeight: 700, fontSize: 13, color: c.text }}>{cv.value}</span>
                    </div>
                  </div>
                  {/* Description */}
                  {cv.description && (
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, flex: 1 }}>
                      {cv.description}
                    </p>
                  )}
                  {/* Behaviour tags */}
                  {(cv.accepted_behavior || cv.unaccepted_behavior) && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-end', maxWidth: 320 }}>
                      {cv.accepted_behavior && cv.accepted_behavior.split(',').map(t => t.trim()).filter(Boolean).map((t, j) => (
                        <span key={j} className="tag-chip">{t}</span>
                      ))}
                      {cv.unaccepted_behavior && cv.unaccepted_behavior.split(',').map(t => t.trim()).filter(Boolean).map((t, j) => (
                        <span key={j} className="tag-chip tag-chip--danger">{t}</span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Market cards ── */}
      {(organisation.target_customer_segments || organisation.major_products_services) && (
        <div style={{ display: 'grid', gridTemplateColumns: organisation.target_customer_segments && organisation.major_products_services ? '1fr 1fr' : '1fr', gap: 16, marginBottom: 20 }}>
          {organisation.target_customer_segments && (
            <MarketCard icon={<Target size={16} color="#9333ea" />} label="Target Customer Segments" text={organisation.target_customer_segments} />
          )}
          {organisation.major_products_services && (
            <MarketCard icon={<Package size={16} color="#ea580c" />} label="Major Products & Services" text={organisation.major_products_services} />
          )}
        </div>
      )}

      {/* ── Quality Policy ── */}
      {organisation.quality_policy && (
        <div style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 12, padding: '22px 26px', marginBottom: 20,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <ShieldCheck size={16} color="#16a34a" />
            <SectionLabel style={{ marginBottom: 0 }}>Quality Policy</SectionLabel>
          </div>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.75 }}>
            {organisation.quality_policy}
          </p>
        </div>
      )}

      {/* ── Locations ── */}
      {locations.length > 0 && (
        <div style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 12, padding: '22px 26px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <MapPin size={15} color="var(--brand)" />
            <SectionLabel style={{ marginBottom: 0 }}>
              Locations <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 13 }}>({locations.length})</span>
            </SectionLabel>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {locations.map((loc) => <LocationRow key={loc.id} loc={loc} />)}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Small components ── */

function MetaPill({ Icon, label, href }) {
  const inner = (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: 'var(--bg-muted)', border: '1px solid var(--border)',
      borderRadius: 20, padding: '5px 12px',
      fontSize: 12, color: href ? 'var(--brand)' : 'var(--text-secondary)',
      fontWeight: 500, whiteSpace: 'nowrap',
    }}>
      <Icon size={12} style={{ flexShrink: 0, color: href ? 'var(--brand)' : 'var(--text-muted)' }} />
      {label}
    </span>
  );
  return href ? <a href={href} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>{inner}</a> : inner;
}

function SectionLabel({ children, style }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
      letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: 12,
      ...style,
    }}>
      {children}
    </div>
  );
}

function QuoteCard({ icon, label, text, accentColor, bgColor, borderColor }) {
  return (
    <div style={{
      background: bgColor, border: `1px solid ${borderColor}`,
      borderRadius: 12, padding: '20px 22px',
      borderLeft: `4px solid ${accentColor}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
        {icon}
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: accentColor }}>{label}</span>
      </div>
      <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.7 }}>{text}</p>
    </div>
  );
}

function MarketCard({ icon, label, text }) {
  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 12, padding: '20px 22px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
        {icon}
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-muted)' }}>{label}</span>
      </div>
      <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.7 }}>{text}</p>
    </div>
  );
}

function LocationRow({ loc }) {
  const address = [loc.address_line1, loc.address_line2].filter(Boolean).join(', ');
  const city    = [loc.city, loc.state, loc.country].filter(Boolean).join(', ');

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: '12px 14px', borderRadius: 8,
      background: 'var(--bg-muted)', border: '1px solid var(--border)',
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
        background: 'var(--brand-light)', border: '1px solid var(--border-strong)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <MapPin size={14} color="var(--brand)" />
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{loc.name}</div>
        {address && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 1 }}>{address}</div>}
        {city && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 1 }}>
            {city}{loc.postal_code ? ` – ${loc.postal_code}` : ''}
          </div>
        )}
        {loc.timezone && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{loc.timezone}</div>}
      </div>
    </div>
  );
}
